/**
 * ProjectRMWWorkspace — the Project Manager's editable risk worksheet.
 *
 * The worksheet is grouped per Risk Area in MUI Accordions. Each accordion
 * contains an editable table of risks where the PM can adjust Impact (1-10),
 * Probability (1-10), Mitigation, Target Date and Status inline. Exposure
 * and Level are recomputed live client-side using the same formula the
 * backend uses (see RiskScoreBadge.ts) so the chip color flips in real time
 * as the PM scrubs values.
 *
 * Actions:
 *   - **Draft save** — persists snapshots for the current OPEN cycle.
 *   - **Submit review** — calls `cycles/:id/submit/`.
 *   - **Open new cycle** — POST to /cycles/ with a `carry_forward_open_risks`
 *     flag (default true) so the new cycle starts pre-populated with the
 *     latest snapshot of every still-open risk.
 *   - **Add risk** — opens a small dialog to pick area + contributor and
 *     persist a new Risk row.
 *
 * View-only fallbacks render when the user is not the assigned PM or an
 * RMW admin (Process Compliance Officer, Employee on the project).
 *
 * The area tables are memoized. Rich-text fields keep each keystroke inside
 * the editor and commit on blur (Save also reads the live HTML), so typing
 * or expanding one risk does not re-render every other row.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import HistoryIcon from "@mui/icons-material/History";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import { fetchData, patchData, postData } from "../../../store/actions";
import { RootState } from "../../../store/Store";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import {
  riskCyclesUrl,
  riskDetailUrl,
  risksUrl,
  riskSnapshotsUrl,
  riskTemplateDetailUrl,
  riskWorksheetDetailUrl,
  submitRiskCycleUrl,
} from "../../../Service/Apis";
import {
  Risk,
  RiskSnapshot,
  RiskTemplate,
  RiskWorksheet,
} from "./Types/RiskManagementTypes";
import RiskScoreBadge, {
  computeExposure,
  levelFromExposure,
} from "./components/RiskScoreBadge";
import { isRmwAdmin } from "./components/rmwRoles";
import { getDefaultFactors } from "./components/defaultFactors";
import {
  normalizeRiskList,
  normalizeWorksheet,
} from "./components/normalizers";
import RiskAreaPanel, {
  EditableRiskRow,
  collectDirtyRows,
  mergeLiveEditorText,
  patchEditableRow,
  TEXT_FIELDS,
  canonicalEditorHtml,
  textFlushKey,
} from "./components/RiskWorksheetTable";
import AddRiskDialog, { AddRiskInput } from "./components/AddRiskDialog";

const EMPTY_ROWS: EditableRiskRow[] = [];

/** Suggest a sensible cycle name given the frequency and today's date. */
const suggestCycleName = (
  frequency: string | undefined,
  date: Date = new Date()
): string => {
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();
  if (frequency === "quarterly") {
    const q = Math.floor(date.getMonth() / 3) + 1;
    return `Q${q} ${year}`;
  }
  return `${month} ${year}`;
};

const toDateInputValue = (iso?: string | null) => (iso ? iso.slice(0, 10) : "");

const buildRowFromRisk = (risk: Risk): EditableRiskRow => {
  const snap = risk.current_snapshot ?? null;
  return {
    risk,
    snapshot: snap,
    draftImpact: Number(snap?.impact ?? 1) || 1,
    draftProbability: Number(snap?.probability ?? 1) || 1,
    draftMitigation: snap?.mitigation_plan ?? "",
    draftTargetDate: toDateInputValue(snap?.target_date ?? null),
    draftStatus: snap?.status ?? "open",
    draftComments: snap?.comments ?? "",
    draftRatingFactors: snap?.rating_factors ?? "",
    draftScenarioJustification: snap?.scenario_justification ?? "",
    isDirty: false,
  };
};

const ProjectRMWWorkspace: React.FC = () => {
  const { worksheetId } = useParams<{ worksheetId: string }>();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );
  const userId = useSelector((state: RootState) => state.auth.userId);

  const [worksheet, setWorksheet] = useState<RiskWorksheet | null>(null);
  const [template, setTemplate] = useState<RiskTemplate | null>(null);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [rowsByArea, setRowsByArea] = useState<Record<string, EditableRiskRow[]>>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Latest row buffer, including updates flushed inside setState updaters,
  // so Save can read text the editor has not yet pushed through a re-render.
  const rowsRef = useRef(rowsByArea);
  rowsRef.current = rowsByArea;
  const flushRegistry = useRef<Map<string, () => string>>(new Map());

  // Add-risk dialog. Field values live in the dialog so typing there does
  // not re-render this page.
  const [addOpen, setAddOpen] = useState(false);
  const [addAreaPreset, setAddAreaPreset] = useState("");
  const [addSaving, setAddSaving] = useState(false);

  // New-cycle dialog
  const [cycleOpen, setCycleOpen] = useState(false);
  const [cycleName, setCycleName] = useState("");
  const [cycleStart, setCycleStart] = useState("");
  const [cycleEnd, setCycleEnd] = useState("");
  const [carryForward, setCarryForward] = useState(true);
  const [cycleSaving, setCycleSaving] = useState(false);
  const [cycleError, setCycleError] = useState("");

  const isAdmin = isRmwAdmin(selectedRole);
  // Compare as strings — backend user IDs may come back as int or UUID, while
  // `userId` is the string we stored in localStorage at login. A mismatched
  // type was silently disabling the edit controls for the actual PM.
  const isAssignedPM = useMemo(
    () =>
      !!userId &&
      worksheet?.current_pm != null &&
      String(worksheet.current_pm) === String(userId),
    [userId, worksheet]
  );
  // Only the assigned PM can edit risk data (impact, probability, status,
  // mitigation, etc.). Admins retain administrative controls (open/close
  // cycles, lock worksheets) but cannot modify risk row content.
  const canEdit =
    !!worksheet &&
    worksheet.is_active &&
    !worksheet.is_locked &&
    isAssignedPM &&
    (worksheet.current_cycle_status === "open" ||
      worksheet.current_cycle_status === "draft" ||
      worksheet.current_cycle_status === "submitted");
  const isCycleSubmitted = worksheet?.current_cycle_status === "submitted";

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const fetchWorksheet = useCallback(async () => {
    if (!worksheetId) return;
    setLoading(true);
    try {
      const wsRaw = await dispatch(fetchData(riskWorksheetDetailUrl(worksheetId)));
      const ws = normalizeWorksheet(wsRaw);
      setWorksheet(ws);
      const [tpl, rls] = await Promise.all([
        ws.template
          ? dispatch(fetchData(riskTemplateDetailUrl(ws.template)))
          : Promise.resolve(null),
        // Ask the API to omit closed risks — the worksheet editor only deals
        // with the currently-active set. Belt-and-suspenders: we also filter
        // on the client below so legacy backends that ignore the query param
        // still render correctly.
        dispatch(
          fetchData(
            `${risksUrl}?worksheet=${worksheetId}&is_closed=false&page_size=500`
          )
        ),
      ]);
      setTemplate(tpl as RiskTemplate);
      const normalized = normalizeRiskList(rls);
      setRisks(normalized.filter((r) => !r.is_closed));
    } catch {
      showToast("Failed to load worksheet.", "error", false);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worksheetId, dispatch]);

  useEffect(() => {
    fetchWorksheet();
  }, [fetchWorksheet]);

  // Rebuild editable row buffer whenever risks change.
  // When a risk's rating_factors snapshot is empty, pre-populate with the
  // template contributor's default factors so the PM has a starting point.
  // IMPORTANT: Preserve any dirty (unsaved) draft values so that adding a
  // new risk (which triggers a refetch) doesn't discard the user's edits.
  useEffect(() => {
    if (!template) return;
    setRowsByArea((prev) => {
      const dirtyLookup = new Map<string, EditableRiskRow>();
      Object.values(prev).forEach((rows) =>
        rows.forEach((r) => {
          if (r.isDirty) dirtyLookup.set(r.risk.id, r);
        })
      );

      const grouped: Record<string, EditableRiskRow[]> = {};
      template.areas.forEach((a) => {
        grouped[a.id] = [];
      });
      risks.forEach((r) => {
        const existing = dirtyLookup.get(r.id);
        if (existing) {
          const bucket = grouped[r.area] ?? [];
          bucket.push({ ...existing, risk: r });
          grouped[r.area] = bucket;
          return;
        }
        const row = buildRowFromRisk(r);
        if (!row.draftRatingFactors) {
          const area = template.areas.find((a) => a.id === r.area);
          if (area) {
            const contrib = area.contributors.find((c) => c.id === r.contributor);
            const raw = contrib?.factors || (contrib ? getDefaultFactors(area.name, contrib.name) : "");
            if (raw) {
              const lines = raw.split("\n").filter((f) => f.trim());
              if (lines.length) {
                row.draftRatingFactors = `<ul>${lines.map((f) => `<li><p>${f}</p></li>`).join("")}</ul>`;
              }
            }
          }
        }
        const bucket = grouped[r.area] ?? [];
        bucket.push(row);
        grouped[r.area] = bucket;
      });
      return grouped;
    });
  }, [risks, template]);

  const overallExposure = useMemo(() => {
    let sum = 0;
    let count = 0;
    Object.values(rowsByArea).forEach((rows) => {
      rows.forEach((row) => {
        sum += computeExposure(row.draftImpact, row.draftProbability);
        count += 1;
      });
    });
    return count ? sum / count : 0;
  }, [rowsByArea]);

  const overallLevel = useMemo(
    () => levelFromExposure(overallExposure),
    [overallExposure]
  );

  // -------------------------------------------------------------------------
  // Row editing
  // -------------------------------------------------------------------------

  const updateRow = useCallback(
    (
      areaId: string,
      riskId: string,
      patch: Partial<EditableRiskRow>
    ) => {
      setRowsByArea((prev) => {
        const rows = prev[areaId];
        if (!rows) return prev;
        let changed = false;
        const nextRows = rows.map((row) => {
          if (row.risk.id !== riskId) return row;
          const next = patchEditableRow(row, patch);
          if (next !== row) changed = true;
          return next;
        });
        if (!changed) return prev;
        const next = { ...prev, [areaId]: nextRows };
        rowsRef.current = next;
        return next;
      });
    },
    []
  );

  const registerFlush = useCallback(
    (key: string, getter: (() => string) | null) => {
      if (getter) flushRegistry.current.set(key, getter);
      else flushRegistry.current.delete(key);
    },
    []
  );

  const commitRiskText = useCallback(
    (areaId: string, riskId: string) => {
      const patch: Partial<EditableRiskRow> = {};
      TEXT_FIELDS.forEach((field) => {
        const read = flushRegistry.current.get(textFlushKey(riskId, field));
        if (!read) return;
        try {
          patch[field] = canonicalEditorHtml(read());
        } catch {
          // Editor was destroyed between expand and collapse.
        }
      });
      if (Object.keys(patch).length) updateRow(areaId, riskId, patch);
    },
    [updateRow]
  );

  const applyLiveEditorHtml = useCallback(() => {
    const merged = mergeLiveEditorText(rowsRef.current, flushRegistry.current);
    if (merged !== rowsRef.current) {
      rowsRef.current = merged;
      setRowsByArea(merged);
    }
    return rowsRef.current;
  }, []);

  const openHistory = useCallback(
    (riskId: string) => {
      navigate(`/pms/risk-management/risk/${riskId}/history`);
    },
    [navigate]
  );

  const openAudit = useCallback(
    (riskId: string) => {
      navigate(`/pms/risk-management/risk/${riskId}/audit`);
    },
    [navigate]
  );

  const dirtyRows = useMemo(() => {
    const out: EditableRiskRow[] = [];
    Object.values(rowsByArea).forEach((rows) =>
      rows.forEach((r) => r.isDirty && out.push(r))
    );
    return out;
  }, [rowsByArea]);

  // -------------------------------------------------------------------------
  // Save / Submit
  // -------------------------------------------------------------------------

  const clearDirtyFlags = useCallback(() => {
    setRowsByArea((prev) => {
      const next: Record<string, EditableRiskRow[]> = {};
      Object.entries(prev).forEach(([areaId, rows]) => {
        next[areaId] = rows.map((r) =>
          r.isDirty ? { ...r, isDirty: false } : r
        );
      });
      return next;
    });
  }, []);

  const handleDraftSave = async (opts?: {
    silentIfEmpty?: boolean;
  }): Promise<boolean> => {
    if (!worksheet?.current_cycle_id) {
      showToast("No open cycle to save into.", "error", false);
      return false;
    }
    // Blur the focused cell so Impact/Probability (committed on blur) land
    // in row state. Rich text is read from the live editors below, because
    // those fields intentionally do not update parent state on each letter.
    (document.activeElement as HTMLElement | null)?.blur?.();
    await new Promise((resolve) => setTimeout(resolve, 0));
    const pending = collectDirtyRows(applyLiveEditorHtml());
    if (!pending.length) {
      if (!opts?.silentIfEmpty) showToast("Nothing to save.", "info", false);
      return true;
    }
    setSaving(true);
    try {
      const currentCycleId = worksheet.current_cycle_id;
      const results = await Promise.allSettled(
        pending.map((row) => {
          const payload = {
            risk: row.risk.id,
            cycle: currentCycleId,
            impact: row.draftImpact,
            probability: row.draftProbability,
            mitigation_plan: row.draftMitigation,
            target_date: row.draftTargetDate || null,
            status: row.draftStatus,
            comments: row.draftComments,
            rating_factors: row.draftRatingFactors,
            scenario_justification: row.draftScenarioJustification,
          };
          // PATCH existing snapshot only if it belongs to the current cycle.
          // A stale snapshot from a previous cycle should not be PATCHed —
          // the backend will upsert a new one for the current cycle.
          const snapshotMatchesCycle =
            row.snapshot?.id &&
            row.snapshot.cycle === currentCycleId;

          if (snapshotMatchesCycle) {
            return dispatch(
              patchData(`${riskSnapshotsUrl}${row.snapshot!.id}/`, payload)
            ).then(() => ({ riskId: row.risk.id, areaId: row.risk.area, snapshot: null as RiskSnapshot | null }));
          }
          return dispatch(postData(riskSnapshotsUrl, payload))
            .then((data: any) => ({ riskId: row.risk.id, areaId: row.risk.area, snapshot: (data ?? null) as RiskSnapshot | null }));
        })
      );

      // Immediately capture newly-created snapshot IDs into row state so a
      // subsequent save before fetchWorksheet completes routes to PATCH.
      const newSnapshots = results
        .filter(
          (r): r is PromiseFulfilledResult<{ riskId: string; areaId: string; snapshot: RiskSnapshot | null }> =>
            r.status === "fulfilled" && r.value.snapshot?.id != null
        )
        .map((r) => r.value);

      if (newSnapshots.length) {
        setRowsByArea((prev) => {
          const next = { ...prev };
          for (const { riskId, areaId, snapshot } of newSnapshots) {
            const rows = next[areaId];
            if (!rows) continue;
            next[areaId] = rows.map((r) =>
              r.risk.id === riskId ? { ...r, snapshot } : r
            );
          }
          return next;
        });
      }

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length) {
        showToast("Failed to save some snapshots.", "error", false);
        clearDirtyFlags();
        fetchWorksheet();
        return false;
      }

      showToast("Draft saved.", "success", false);
      clearDirtyFlags();
      fetchWorksheet();
      return true;
    } catch {
      showToast("Failed to save some snapshots.", "error", false);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!worksheet?.current_cycle_id) return;
    // Make it explicit that submission is a soft milestone — the cycle stays
    // open and editing remains possible until an RMW Admin closes it. PMs may
    // re-submit any number of times to flag fresh edits for reviewers.
    const ok = window.confirm(
      isCycleSubmitted
        ? "Re-submit this cycle?\n\n" +
            "This re-stamps the cycle's submitted timestamp so reviewers " +
            "know there are new edits to look at. The cycle stays OPEN and " +
            "you can keep editing afterwards."
        : "Submit this cycle for review?\n\n" +
            "This stamps the cycle as 'submitted' and notifies reviewers, but the " +
            "cycle stays OPEN — you (and the admin) can still edit risks, add new " +
            "ones, and update snapshots. The cycle is only locked when an RMW " +
            "Admin runs the explicit Close action."
    );
    if (!ok) return;
    setSubmitting(true);
    try {
      // Flush in-progress editors, then persist, before the cycle is stamped.
      const saved = await handleDraftSave({ silentIfEmpty: true });
      if (!saved) {
        setSubmitting(false);
        return;
      }
      await dispatch(postData(submitRiskCycleUrl(worksheet.current_cycle_id), {}));
      showToast(
        isCycleSubmitted
          ? "Cycle re-submitted. Reviewers will see the latest edits."
          : "Review submitted. Cycle remains open for further edits.",
        "success",
        false
      );
      fetchWorksheet();
    } catch {
      showToast("Failed to submit cycle.", "error", false);
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Add risk
  // -------------------------------------------------------------------------

  const openAdd = useCallback((areaId?: string) => {
    setAddAreaPreset(areaId ?? "");
    setAddOpen(true);
  }, []);

  const getUsedContributorIds = useCallback((areaId: string) => {
    const rows = rowsRef.current[areaId] ?? EMPTY_ROWS;
    return new Set(rows.map((row) => row.risk.contributor));
  }, []);

  const handleAddRisk = useCallback(
    async (input: AddRiskInput) => {
      if (!worksheetId || !input.area || !input.contributor) {
        showToast("Area and contributor are required.", "error", false);
        return;
      }
      if (getUsedContributorIds(input.area).has(input.contributor)) {
        showToast(
          "This contributor already has an active risk in this area.",
          "error",
          false
        );
        return;
      }
      setAddSaving(true);
      try {
        const riskPayload: Record<string, any> = {
          worksheet: worksheetId,
          area: input.area,
          contributor: input.contributor,
        };
        const newRisk: any = await dispatch(postData(risksUrl, riskPayload));

        const hasSnapshotData =
          input.ratingFactors || input.scenario || input.mitigation;
        if (hasSnapshotData && worksheet?.current_cycle_id && newRisk?.id) {
          try {
            await dispatch(
              postData(riskSnapshotsUrl, {
                risk: newRisk.id,
                cycle: worksheet.current_cycle_id,
                impact: 1,
                probability: 1,
                rating_factors: input.ratingFactors,
                scenario_justification: input.scenario,
                mitigation_plan: input.mitigation,
              })
            );
          } catch {
            // Non-critical — snapshot will be created on next draft save.
          }
        }

        showToast("Risk added.", "success", false);
        setAddOpen(false);
        fetchWorksheet();
      } catch {
        showToast("Failed to add risk.", "error", false);
      } finally {
        setAddSaving(false);
      }
    },
    [
      worksheetId,
      worksheet?.current_cycle_id,
      dispatch,
      showToast,
      fetchWorksheet,
      getUsedContributorIds,
    ]
  );

  // -------------------------------------------------------------------------
  // Open new cycle
  // -------------------------------------------------------------------------

  const openCycleDialog = () => {
    const suggested = suggestCycleName(worksheet?.review_frequency);
    setCycleName(suggested);
    const today = new Date();
    setCycleStart(today.toISOString().slice(0, 10));
    const end = new Date(today);
    end.setMonth(
      end.getMonth() + (worksheet?.review_frequency === "quarterly" ? 3 : 1)
    );
    setCycleEnd(end.toISOString().slice(0, 10));
    setCarryForward(true);
    setCycleError("");
    setCycleOpen(true);
  };

  const handleCreateCycle = async () => {
    if (!worksheetId || !cycleName.trim() || !cycleStart || !cycleEnd) {
      setCycleError("All cycle fields are required.");
      return;
    }
    if (
      worksheet?.current_cycle_name &&
      cycleName.trim().toLowerCase() ===
        worksheet.current_cycle_name.trim().toLowerCase()
    ) {
      setCycleError(
        `A cycle named "${worksheet.current_cycle_name}" already exists for this worksheet. Please enter a different cycle name.`
      );
      return;
    }
    if (cycleEnd < cycleStart) {
      setCycleError("Period end must be on or after period start.");
      return;
    }
    setCycleError("");
    setCycleSaving(true);
    try {
      await dispatch(
        postData(riskCyclesUrl, {
          worksheet: worksheetId,
          name: cycleName.trim(),
          period_start: cycleStart,
          period_end: cycleEnd,
          carry_forward_open_risks: carryForward,
        })
      );
      showToast("New review cycle opened.", "success", false);
      setCycleOpen(false);
      fetchWorksheet();
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        data?.name?.[0] ||
        data?.non_field_errors?.[0] ||
        data?.detail ||
        "";
      if (
        msg.toLowerCase().includes("unique") ||
        msg.toLowerCase().includes("already exists")
      ) {
        setCycleError(
          `A cycle with this name already exists for this worksheet. Please enter a different cycle name.`
        );
      } else {
        setCycleError(
          msg || "Failed to open new cycle. Please check the details and try again."
        );
      }
    } finally {
      setCycleSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Delete (close) a risk
  // -------------------------------------------------------------------------

  const handleCloseRisk = useCallback(
    async (risk: Risk) => {
      if (
        !window.confirm(
          `Close risk "${risk.contributor_name ?? risk.contributor}"?`
        )
      ) {
        return;
      }
      try {
        await dispatch(
          patchData(riskDetailUrl(risk.id), {
            is_closed: true,
            closed_reason: "Closed from worksheet",
          })
        );
        // Optimistically drop the row from local state so the table updates
        // immediately, before the background refetch returns.
        setRisks((prev) => prev.filter((r) => r.id !== risk.id));
        setRowsByArea((prev) => {
          const next: Record<string, EditableRiskRow[]> = {};
          Object.entries(prev).forEach(([areaId, rows]) => {
            next[areaId] = rows.filter((r) => r.risk.id !== risk.id);
          });
          rowsRef.current = next;
          return next;
        });
        const flushPrefix = `${risk.id}::`;
        Array.from(flushRegistry.current.keys()).forEach((key) => {
          if (key.startsWith(flushPrefix)) flushRegistry.current.delete(key);
        });
        showToast("Risk closed.", "success", false);
        fetchWorksheet();
      } catch {
        showToast("Failed to close risk.", "error", false);
      }
    },
    [dispatch, showToast, fetchWorksheet]
  );

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const renderHeader = () => {
    if (!worksheet) return null;
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" color="text.secondary">
                Project
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {worksheet.project_name ?? worksheet.project}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PM: <b>{worksheet.current_pm_name ?? "Unassigned"}</b> •{" "}
                Template: {worksheet.template_name ?? "—"} •{" "}
                Frequency: {worksheet.review_frequency}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="overline" color="text.secondary">
                Current Cycle
              </Typography>
              <Typography variant="h6">
                {worksheet.current_cycle_name ?? "No open cycle"}
              </Typography>
              {/* A "submitted" cycle is still editable until an admin closes
                  it, so we show OPEN to avoid implying the cycle is locked.
                  We tack on a small tick when the PM has submitted at least
                  once, so the milestone is still visible. */}
              <Chip
                size="small"
                label={
                  worksheet.current_cycle_status === "submitted"
                    ? "OPEN ✓ submitted"
                    : (worksheet.current_cycle_status ?? "—").toUpperCase()
                }
                color={
                  worksheet.current_cycle_status === "open" ||
                  worksheet.current_cycle_status === "submitted"
                    ? "warning"
                    : worksheet.current_cycle_status === "closed"
                    ? "default"
                    : "default"
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="overline" color="text.secondary">
                Overall Risk
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <RiskScoreBadge
                  level={overallLevel}
                  exposure={overallExposure}
                  size="medium"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={2} sx={{ textAlign: { md: "right" } }}>
              {(isAdmin || isAssignedPM) && (
                <Button
                  variant="outlined"
                  startIcon={<TimelineIcon />}
                  onClick={openCycleDialog}
                  sx={{ mb: 1 }}
                  fullWidth
                >
                  Open new cycle
                </Button>
              )}
              <Button
                variant="text"
                size="small"
                startIcon={<HistoryIcon />}
                onClick={() =>
                  navigate(`/pms/risk-management/summary/${worksheet.project}`)
                }
                fullWidth
              >
                View summary
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };


  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // The template areas render before the row buffer is filled, which flashes
  // a full page of "0 risks" while the heavy table is still being built.
  const waitingForRows =
    !!template?.areas?.length && Object.keys(rowsByArea).length === 0;

  if (loading || waitingForRows) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "calc(100vh - 160px)",
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!worksheet) {
    return (
      <Container maxWidth="md" sx={{ mt: 12 }}>
        <Alert severity="error">Worksheet not found.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>Risk Worksheet</PageTitle>

      {renderHeader()}

      {worksheet.is_locked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This worksheet is locked. Editing is disabled.
        </Alert>
      )}
      {!canEdit && !worksheet.is_locked && (() => {
        // Differentiate the three reasons `canEdit` can be false so the user
        // sees an *actionable* message instead of a confusing "only the PM
        // can edit" prompt when they actually ARE the PM but the cycle is
        // simply not open yet.
        const cycleStatus = worksheet.current_cycle_status;
        const noOpenCycle = !cycleStatus || cycleStatus === "closed";
        const isReviewerOnly = !isAssignedPM;

        if (isReviewerOnly) {
          return (
            <Alert severity="info" sx={{ mb: 2 }}>
              You have read-only access. Only the assigned PM can edit risk
              data in this worksheet.
            </Alert>
          );
        }
        if (!worksheet.is_active) {
          return (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This worksheet has been disabled. An RMW Admin needs to
              re-enable it before editing is possible.
            </Alert>
          );
        }
        if (noOpenCycle) {
          return (
            <Alert severity="info" sx={{ mb: 2 }}>
              There is no open review cycle for this worksheet. Click{" "}
              <b>Open new cycle</b> above to start a new review and begin
              editing risks.
            </Alert>
          );
        }
        // Fallback (cycle is in some other state like `reappeared`).
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            The current cycle is in <b>{cycleStatus}</b> state and cannot be
            edited. Open a new cycle to continue.
          </Alert>
        );
      })()}

      {(template?.areas ?? []).map((area) => (
        <RiskAreaPanel
          key={area.id}
          area={area}
          rows={rowsByArea[area.id] ?? EMPTY_ROWS}
          canEdit={canEdit}
          onUpdate={updateRow}
          onCommitText={commitRiskText}
          onClose={handleCloseRisk}
          onOpenHistory={openHistory}
          onOpenAudit={openAudit}
          onAdd={openAdd}
          registerFlush={registerFlush}
        />
      ))}

      {isAssignedPM && (
        <Paper
          elevation={3}
          sx={{
            position: "sticky",
            bottom: 16,
            mt: 3,
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {dirtyRows.length > 0
              ? `${dirtyRows.length} unsaved change(s)`
              : "All changes saved."}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            disabled={!canEdit}
            onClick={() => openAdd()}
          >
            Add risk
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />
            }
            disabled={!canEdit || saving || submitting}
            onClick={() => {
              void handleDraftSave();
            }}
          >
            Save draft
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendOutlinedIcon />
              )
            }
            disabled={!canEdit || submitting}
            onClick={handleSubmit}
          >
            {isCycleSubmitted ? "Re-submit ✓" : "Submit review"}
          </Button>
        </Paper>
      )}

      {addOpen && template && (
        <AddRiskDialog
          areas={template.areas}
          initialAreaId={addAreaPreset}
          saving={addSaving}
          getUsedContributorIds={getUsedContributorIds}
          onClose={() => setAddOpen(false)}
          onSubmit={handleAddRisk}
        />
      )}

      {/* New cycle dialog */}
      <Dialog
        open={cycleOpen}
        onClose={() => !cycleSaving && setCycleOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Open new review cycle</DialogTitle>
        <DialogContent dividers>
          {cycleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {cycleError}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="Cycle name"
              fullWidth
              value={cycleName}
              onChange={(e) => {
                setCycleName(e.target.value);
                if (cycleError) setCycleError("");
              }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Period start"
                type="date"
                value={cycleStart}
                onChange={(e) => setCycleStart(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Period end"
                type="date"
                value={cycleEnd}
                onChange={(e) => setCycleEnd(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={carryForward}
                  onChange={(e) => setCarryForward(e.target.checked)}
                />
              }
              label="Carry forward open risks from previous cycle"
            />
            <Divider />
            <Typography variant="caption" color="text.secondary">
              When enabled, every risk on this worksheet whose latest status is
              not <i>closed</i> is cloned into the new cycle so you start with
              the previous impact / probability / mitigation in place.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCycleOpen(false)} disabled={cycleSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateCycle}
            disabled={cycleSaving}
            startIcon={cycleSaving ? <CircularProgress size={16} /> : null}
          >
            Open cycle
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectRMWWorkspace;
